import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import utils from "../utils/utils";

/**
 * 邀请码地址栏持久化。
 *
 * 需求(运营/阿南):开关 SysSetting.showInviteCodeInUrl 打开时,只要用户「未登录」且已捕获到 invitedCode,
 * SPA 内每个页面的地址栏都要一直带上 ?p=<code>(落地首页、进注册页、甚至有缓存直接访问都要带);
 * 登录之后不再强制(可自然掉)。
 *
 * 实现:监听路由变化,满足条件而当前 URL 缺 ?p= 时,以 replace 方式把 ?p=<code> 补回(保留原有 query/hash)。
 * 幂等:已带正确 ?p= 时直接返回,避免 navigate 回环。
 */
export function useInviteCodeUrlPersist(): void {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      // 1) 开关关 → 维持原样,不介入
      if (!utils.getObject("showInviteCodeInUrl")) return;

      // 2) 已登录(有有效 token)→ 登录后可不带,不强制
      const token = utils.getObject("token");
      const loggedIn = !!token && token !== "null" && String(token).length >= 10;
      if (loggedIn) return;

      // 3) 【投放保护】存在渠道/落地代理信号(ch / launchAgentId)时不强制邀请码,
      //    避免用(可能是旧的)invitedCode 顶出 ?p= 而覆盖投放归属(注册优先级 p > launchAgentId)。
      //    纯邀请链接 /c-<code> 不带 ch,不受影响。
      const w = window as unknown as { __ch?: string };
      if (utils.getObject("ch") || w.__ch || utils.getObject("launchAgentId")) return;

      // 4) 无邀请码 → 无可带
      const code = utils.getObject("invitedCode");
      if (!code || typeof code !== "string") return;

      // 5) 已经带了正确的 ?p= → 幂等返回,防回环
      const params = new URLSearchParams(location.search);
      if (params.get("p") === code) return;

      // 6) 补回 ?p=<code>,保留其余 query 与 hash
      params.set("p", code);
      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
          hash: location.hash,
        },
        { replace: true }
      );
    } catch {
      // 忽略,不影响页面
    }
  }, [location.pathname, location.search, location.hash, navigate]);
}
